import {
  formatResponsePath,
  parseResponsePath,
  responsePathFromTemplate,
  responseTemplateForPath,
  resolveToolResponseProjection,
  validateToolResponseProjection,
  type ResponsePathToken,
  type ToolResponseProjection,
} from '@reach/shared-types/tool-response-projection';
import { INPUT_NAME_PATTERN } from './tool-inputs';

export interface ResponseFieldRow {
  name: string;
  path: string;
}

export function responseMappingRepresentabilityIssue(mapping: unknown): string | null {
  if (mapping === null || mapping === undefined) return null;
  if (Array.isArray(mapping) || typeof mapping !== 'object') {
    return 'This mapping is not a set of fields';
  }

  for (const [name, value] of Object.entries(mapping)) {
    if (!INPUT_NAME_PATTERN.test(name)) {
      return `Field "${name}" has a name the builder can't edit`;
    }
    if (value !== null && typeof value === 'object') {
      const shape = Array.isArray(value) ? 'a list' : 'a nested group of fields';
      return `Field "${name}" maps ${shape} — the builder only shows a flat list of fields`;
    }
    if (typeof value !== 'string' || responsePathFromTemplate(value) === null) {
      return `Field "${name}" isn't a plain {{response.…}} value`;
    }
  }
  return null;
}

export function isResponseMappingRepresentable(mapping: unknown): boolean {
  return responseMappingRepresentabilityIssue(mapping) === null;
}

export function responseMappingToRows(mapping: unknown): ResponseFieldRow[] {
  if (!isResponseMappingRepresentable(mapping) || !mapping) return [];
  return Object.entries(mapping).map(([name, value]) => ({
    name,
    path: typeof value === 'string' ? (responsePathFromTemplate(value) ?? '') : '',
  }));
}

export function rowsToResponseMapping(rows: ResponseFieldRow[]): ToolResponseProjection | null {
  const completeRows = rows.filter(row => row.name.trim() !== '' && row.path.trim() !== '');
  if (completeRows.length === 0) return null;
  return Object.fromEntries(
    completeRows.flatMap(row => {
      const template = responseTemplateForPath(row.path.trim());
      return template === null ? [] : [[row.name.trim(), template]];
    }),
  ) as ToolResponseProjection;
}

export interface ResponseRowIssue {
  index: number;
  field: 'name' | 'path';
  message: string;
  isBlocking: boolean;
}

export function validateResponseRows(
  rows: ResponseFieldRow[],
  parsedSample?: unknown,
): ResponseRowIssue[] {
  const issues: ResponseRowIssue[] = [];
  const seenNames = new Set<string>();
  const sampleIsPresent = parsedSample !== undefined;

  rows.forEach((row, index) => {
    const name = row.name;
    if (!name.trim()) {
      issues.push({ index, field: 'name', message: 'Field name is required', isBlocking: true });
      return;
    }
    if (!INPUT_NAME_PATTERN.test(name)) {
      issues.push({
        index,
        field: 'name',
        message: 'Use letters, numbers, _ or - only',
        isBlocking: true,
      });
      return;
    }
    if (seenNames.has(name)) {
      issues.push({
        index,
        field: 'name',
        message: `Duplicate field "${name}"`,
        isBlocking: true,
      });
      return;
    }
    seenNames.add(name);

    const path = row.path.trim();
    if (!path) {
      issues.push({ index, field: 'path', message: 'Response path is required', isBlocking: true });
      return;
    }
    const parsedPath = parseResponsePath(path);
    if (!parsedPath.success) {
      issues.push({
        index,
        field: 'path',
        message: parsedPath.message,
        isBlocking: true,
      });
      return;
    }
    const template = responseTemplateForPath(path);
    const sampleIssue =
      sampleIsPresent && template
        ? resolveToolResponseProjection({ value: template }, parsedSample).issues[0]
        : undefined;
    if (sampleIssue) {
      issues.push({
        index,
        field: 'path',
        message:
          sampleIssue.code === 'missing_path'
            ? `Nothing at ${path} in your sample response — this field would be left out`
            : `${path} cannot be resolved against this sample response — this field would be left out`,
        isBlocking: false,
      });
    }
  });

  return issues;
}

export function validateAdvancedResponseMapping(value: unknown): string | null {
  if (value === null) return null;
  const issue = validateToolResponseProjection(value)[0];
  if (!issue) return null;
  const outputPath = issue.path.length > 0 ? ` at ${issue.path.join('.')}` : '';
  return `${issue.message}${outputPath}`;
}

export type SampleValueKind = 'text' | 'number' | 'true/false' | 'list' | 'group' | 'empty';

export interface SampleResponsePath {
  path: string;
  kind: SampleValueKind;
}

function responsePathLeaf(path: string): string | undefined {
  const parsed = parseResponsePath(path);
  return parsed.success
    ? [...parsed.tokens].reverse().find(token => token.type === 'property')?.key
    : undefined;
}

export function responseOutputNameForPath(path: string): string {
  const leaf = responsePathLeaf(path);
  const words = (leaf ?? 'field').split(/[^A-Za-z0-9]+/).filter(Boolean);
  const [first = 'field', ...rest] = words;
  const camelCaseLeaf = [
    first.charAt(0).toLowerCase() + first.slice(1),
    ...rest.map(word => word.charAt(0).toUpperCase() + word.slice(1)),
  ].join('');
  return /^[A-Za-z_]/.test(camelCaseLeaf) ? camelCaseLeaf : `field${camelCaseLeaf}`;
}

export function likelyIdentifierPath(suggestions: SampleResponsePath[]): string | null {
  const score = (path: string): number => {
    const leaf = responsePathLeaf(path) ?? '';
    const normalizedLeaf = leaf.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    if (normalizedLeaf === 'engagementid' || normalizedLeaf === 'engagement_id') return 100;
    if (/^(appointment|booking|job|visit|work_?order)_?id$/.test(normalizedLeaf)) return 90;
    if (normalizedLeaf === 'id') return 80;
    if (/_id$/i.test(leaf) || /Id$/.test(leaf)) return 60;
    return 0;
  };

  return (
    suggestions
      .filter(suggestion => {
        const parsedPath = parseResponsePath(suggestion.path);
        const suggestionIsScalar = suggestion.kind === 'text' || suggestion.kind === 'number';
        const suggestionTraversesArray =
          parsedPath.success && parsedPath.tokens.some(token => token.type === 'each');
        return parsedPath.success && suggestionIsScalar && !suggestionTraversesArray;
      })
      .map(suggestion => ({ path: suggestion.path, score: score(suggestion.path) }))
      .filter(candidate => candidate.score > 0)
      .sort((left, right) => right.score - left.score)[0]?.path ?? null
  );
}

function sampleValueKind(value: unknown): SampleValueKind {
  if (value === null) return 'empty';
  if (Array.isArray(value)) return 'list';
  switch (typeof value) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'true/false';
    case 'object':
      return 'group';
    default:
      return 'text';
  }
}

const MAX_SAMPLE_PATH_DEPTH = 6;
const MAX_SAMPLE_ARRAY_ITEMS = 20;

export function sampleResponsePaths(sample: unknown): SampleResponsePath[] {
  const paths = new Map<string, SampleResponsePath>();

  const walk = (node: unknown, tokens: ResponsePathToken[], depth: number) => {
    if (depth > MAX_SAMPLE_PATH_DEPTH) return;
    if (Array.isArray(node)) {
      for (const item of node.slice(0, MAX_SAMPLE_ARRAY_ITEMS)) {
        walk(item, [...tokens, { type: 'each' }], depth + 1);
      }
      return;
    }
    if (node === null || typeof node !== 'object') return;

    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      const childTokens: ResponsePathToken[] = [...tokens, { type: 'property', key }];
      const path = formatResponsePath(childTokens);
      paths.set(path, { path, kind: sampleValueKind(value) });
      walk(value, childTokens, depth + 1);
    }
  };

  walk(sample, [], 0);
  return [...paths.values()];
}
